import type { AppSettings } from '@/types';

/**
 * Which settings an imported backup may set, and with what values (STORY-331).
 *
 * importAppData used to spread `data.settings` from the file straight into
 * saveSettings, so a backup could write ANY key into the user's settings row —
 * including tool_shortlist / disabled_tools, which the backend forwards to the
 * MCP server's global tool list. STORY-326 gated that forward to admins on the
 * server; this is the client half: a backup sets only AppSettings keys, each
 * with a value of its declared type, and nothing else leaves the browser.
 *
 * The single source of truth is the AppSettings interface. This table cannot
 * drift from it silently: the mapped type below makes `tsc` (CI's typecheck)
 * fail when AppSettings gains a key with no validator or the table names a key
 * AppSettings does not have, and `oneOf` fails when a union gains a member the
 * validator does not accept, or lists one the union does not have.
 */

type Validator<T> = (value: unknown) => value is T;

type SettingsValidators = {
  [K in keyof AppSettings]-?: Validator<NonNullable<AppSettings[K]>>;
};

/**
 * A validator for a string-literal union. The rest parameter is `[]` only when
 * `values` covers all of T, so a missing member is a compile error naming it.
 */
function oneOf<T extends string>() {
  return <V extends readonly T[]>(
    values: V,
    ..._everyMember: [Exclude<T, V[number]>] extends [never] ? [] : [missing: Exclude<T, V[number]>]
  ): Validator<T> =>
    (value: unknown): value is T =>
      typeof value === 'string' && (values as readonly string[]).includes(value);
}

const isString: Validator<string> = (value: unknown): value is string => typeof value === 'string';
const isBoolean: Validator<boolean> = (value: unknown): value is boolean => typeof value === 'boolean';

const VALIDATORS: SettingsValidators = {
  language: oneOf<AppSettings['language']>()(['ru', 'en']),
  theme: oneOf<AppSettings['theme']>()(['light', 'dark', 'system']),
  fontSize: oneOf<AppSettings['fontSize']>()(['small', 'medium', 'large']),
  codeTheme: isString,
  autoSave: isBoolean,
  showTimestamps: isBoolean,
  enableNotifications: isBoolean,
  llmMode: oneOf<AppSettings['llmMode']>()(['auto', 'single']),
  selectedLLMProvider: isString,
  clusterMode: oneOf<NonNullable<AppSettings['clusterMode']>>()(['single', 'sequential', 'parallel']),
  selectedClusterIds: (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((id) => typeof id === 'string'),
  synthesisModeOnMulti: oneOf<NonNullable<AppSettings['synthesisModeOnMulti']>>()(['synthesise', 'concat']),
};

/** Every key a backup may set — derived from the validator table, not listed twice. */
export const IMPORTABLE_SETTINGS_KEYS = Object.keys(VALIDATORS) as Array<keyof AppSettings>;

export interface SanitizedSettings {
  /** Known keys with valid values, safe to merge and send. */
  accepted: Partial<AppSettings>;
  /** Keys from the file that were not applied: unknown, or of the wrong type. */
  rejected: string[];
}

/**
 * Keep only known AppSettings keys whose values have the declared type.
 *
 * The key lookup is an OWN-property check on purpose: `key in VALIDATORS`
 * would find `constructor` and `__proto__` on Object.prototype, and
 * `VALIDATORS.constructor` is `Object`, which returns a truthy object for any
 * input — a file could then smuggle a `constructor` key through.
 */
export function sanitizeImportedSettings(raw: unknown): SanitizedSettings {
  const accepted: Record<string, unknown> = {};
  const rejected: string[] = [];
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { accepted: {}, rejected };
  }

  for (const key of Object.keys(raw)) {
    const value = (raw as Record<string, unknown>)[key];
    const validate = Object.prototype.hasOwnProperty.call(VALIDATORS, key)
      ? (VALIDATORS as Record<string, Validator<unknown>>)[key]
      : undefined;
    if (validate && validate(value)) {
      // Arrays are copied so the stored settings never alias the parsed file.
      accepted[key] = Array.isArray(value) ? [...value] : value;
    } else {
      rejected.push(key);
    }
  }
  return { accepted: accepted as Partial<AppSettings>, rejected };
}
